import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CreateOrPutStudentJobOffer, StudentJobOffer } from '@thro-types/jobmarket';

import { AxiosRequestConfig } from 'axios';

import {
  createStudentJobOffering,
  deleteStudentJobOffering,
  getStudentJobOffering,
  getStudentJobOfferings,
  updateStudentJobOffer
} from 'api/jobmarketService';

/**
 * A custom hook for all studentJobOfferings from the Jobmarket Service.
 *
 * @returns UseQueryResult<StudentJobOffer[], Error>
 */

export const useStudentJobOfferingsQuery = (config?: AxiosRequestConfig) => {
  const query = useQuery({
    queryKey: ['studentJobOfferings', config ? JSON.stringify(config.params) : ''],
    queryFn: () => {
      const jobOfferings = getStudentJobOfferings(config);
      return jobOfferings;
    },
    placeholderData: keepPreviousData
  });

  return query;
};

export const useStudentJobOfferingQuery = (id: string, config?: AxiosRequestConfig) => {
  const query = useQuery({
    queryKey: ['studentJobOfferings', id, config ? JSON.stringify(config.params) : ''],
    queryFn: () => {
      if (!id) {
        return;
      }
      const jobOffering = getStudentJobOffering(id, config);
      return jobOffering;
    },
    enabled: !!id
  });

  return query;
};

export const useStudentJobOfferingsMutation = (config?: AxiosRequestConfig) => {
  const queryClient = useQueryClient();

  const queryKey = ['studentJobOfferings'];
  const create = useMutation<StudentJobOffer, Error, CreateOrPutStudentJobOffer>({
    mutationFn: async args => {
      return await createStudentJobOffering(args, config);
    },
    onSuccess: data => {
      queryClient.invalidateQueries({ queryKey });
    },
    onError: data => {}
  });

  const update = useMutation<
    StudentJobOffer,
    Error,
    { id: string; data: CreateOrPutStudentJobOffer }
  >({
    mutationFn: async args => {
      return await updateStudentJobOffer(args.id, args.data, config);
    },
    onSuccess: data => {
      queryClient.invalidateQueries({ queryKey });
    },
    onError: data => {}
  });

  const deletion = useMutation<StudentJobOffer, Error, string>({
    mutationFn: async args => {
      return await deleteStudentJobOffering(args, config);
    },
    onSuccess: data => {
      queryClient.invalidateQueries({ queryKey });
    },
    onError: data => {}
  });

  return { update, create, deletion } as const;
};
